import type { IDraftListing } from './draft-listing.types';
import { createIdbKvStore } from '../storage/idb-kv-store.util';

/**
 * Persists the in-progress host-wizard draft (and its shelved-drafts stack)
 * in IndexedDB instead of plain `localStorage`. Built on the shared
 * `createIdbKvStore` abstraction this pattern was later generalized into
 * (see `idb-kv-store.util.ts`) — this was the original hand-rolled
 * reference implementation; folded onto the shared one for consistency,
 * still on its own database so this never shares a version/upgrade path
 * with the shared `cnt-landing-kv` store the rest of the app's ~30 other
 * domains use.
 */
const LEGACY_DRAFT_KEY = 'cnt-listing-draft';
const LEGACY_SHELVED_KEY = 'cnt-shelved-drafts';

const DB_NAME = 'cnt-landing-draft';
const STORE = 'draft-state';
const DB_VERSION = 1;

const ACTIVE_KEY = 'active';
const SHELVED_KEY = 'shelved';

const activeDraftStore = createIdbKvStore<IDraftListing>({
  dbName: DB_NAME,
  storeName: STORE,
  version: DB_VERSION,
  legacyKeys: { [ACTIVE_KEY]: LEGACY_DRAFT_KEY },
  reviveLegacy: (_key, parsed) =>
    parsed && typeof (parsed as IDraftListing).id === 'string' ? (parsed as IDraftListing) : undefined,
});
const shelvedDraftsStore = createIdbKvStore<IDraftListing[]>({
  dbName: DB_NAME,
  storeName: STORE,
  version: DB_VERSION,
  legacyKeys: { [SHELVED_KEY]: LEGACY_SHELVED_KEY },
  reviveLegacy: (_key, parsed) => (Array.isArray(parsed) ? (parsed as IDraftListing[]) : undefined),
});

/**
 * Idempotent boot loader. Migrates the legacy `cnt-listing-draft` /
 * `cnt-shelved-drafts` localStorage keys into IDB on first run, then drops
 * them, and hydrates the synchronous in-memory cache. Safe to call multiple
 * times — only the first call per store does work.
 */
export function initDraftStore(): Promise<void> {
  return Promise.all([activeDraftStore.init(), shelvedDraftsStore.init()]).then(() => undefined);
}

/** Synchronous; reflects the cache hydrated by `initDraftStore()`. */
export function readActiveDraft(): IDraftListing | null {
  return activeDraftStore.read(ACTIVE_KEY) ?? null;
}

/** Synchronous; reflects the cache hydrated by `initDraftStore()`. */
export function readShelvedDrafts(): IDraftListing[] {
  return shelvedDraftsStore.read(SHELVED_KEY) ?? [];
}

/**
 * Persist the active draft (or clear it when `null`). Updates the
 * synchronous cache immediately; the returned promise resolves once IDB
 * acknowledges the write — reject path means storage is full or unavailable.
 */
export async function writeActiveDraft(draft: IDraftListing | null): Promise<void> {
  if (draft) await activeDraftStore.write(ACTIVE_KEY, draft);
  else await activeDraftStore.delete(ACTIVE_KEY);
}

/**
 * Persist the shelved-drafts stack. Updates the synchronous cache
 * immediately; the returned promise resolves once IDB acknowledges the write.
 */
export async function writeShelvedDrafts(stack: IDraftListing[]): Promise<void> {
  await shelvedDraftsStore.write(SHELVED_KEY, stack);
}

/** Drafts put away when an account signs out, under that account's email, so
 *  the next person on the device doesn't see them and the same account gets
 *  them back on sign-in. Same store as the shelf (`init()` loads every key). */
function parkedKey(email: string): string {
  return `parked:${email.trim().toLowerCase()}`;
}

/** Synchronous; reflects the cache hydrated by `initDraftStore()`. */
export function readParkedDrafts(email: string): IDraftListing[] {
  return shelvedDraftsStore.read(parkedKey(email)) ?? [];
}

/** Park an account's drafts, or drop its parked drafts when `drafts` is empty. */
export async function writeParkedDrafts(email: string, drafts: IDraftListing[]): Promise<void> {
  if (drafts.length) await shelvedDraftsStore.write(parkedKey(email), drafts);
  else await shelvedDraftsStore.delete(parkedKey(email));
}

/** Test-only: resets the module-level cache and init state. Production code
 *  never calls this — the store otherwise persists for the life of the
 *  module, which is what lets `readActiveDraft()` stay synchronous. Unit
 *  tests that construct multiple service instances need a way back to a
 *  clean slate between cases. */
export function __resetDraftStoreForTesting(): void {
  activeDraftStore.__resetForTesting();
  shelvedDraftsStore.__resetForTesting();
}
